import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Lock, Mail, ShieldCheck } from "lucide-react";
import logo from "../assets/spacem8-logo.png";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { useToast } from "../contexts/ToastContext";

export default function Login({ isDark }) {
  const { signIn, user, loading } = useAuth();
  const toast = useToast();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);

  const from = location.state?.from?.pathname || "/";

  if (!loading && user) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      await signIn(email.trim(), password);
    } catch (err) {
      setError(err.message || "Unable to sign in.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordReset = async () => {
    setError("");

    if (!email.trim()) {
      setError("Enter your email address first, then request a reset link.");
      return;
    }

    if (!supabase) {
      setError("SpaceM8 is missing its Supabase configuration.");
      return;
    }

    setIsSendingReset(true);

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/update-password`,
    });

    setIsSendingReset(false);

    if (resetError) {
      setError(resetError.message || "Unable to send the password reset email.");
      return;
    }

    toast.success(
      "Reset email sent",
      "Open the email link and you will be taken to the password update screen."
    );
  };

  return (
    <main
      className={`min-h-screen px-4 py-8 ${
        isDark
          ? "bg-slate-950 text-white"
          : "bg-gradient-to-br from-[#f8fbff] via-[#eef6ff] to-[#f3f9ff] text-slate-900"
      }`}
    >
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-6xl items-center justify-center">
        <div
          className={`grid w-full overflow-hidden rounded-3xl border shadow-sm lg:grid-cols-[1fr_420px] ${
            isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
          }`}
        >
          <section className={`p-8 md:p-12 ${isDark ? "bg-slate-900" : "bg-sky-50"}`}>
            <img src={logo} alt="SpaceM8" className="h-20 w-auto object-contain" />
            <div className="mt-10 max-w-xl">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
                Secure Workspace
              </p>
              <h1 className={`mt-3 text-4xl font-semibold ${isDark ? "text-white" : "text-slate-950"}`}>
                Sign in to manage your property portfolio.
              </h1>
              <p className={`mt-4 text-sm leading-6 ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                Use your approved SpaceM8 login to access dashboards, invoices, tenants,
                maintenance tickets, messages, reports, and assistant insights.
              </p>
            </div>

            <div className="mt-10 grid gap-4 md:grid-cols-3">
              {["Supabase Auth", "Role Access", "Protected Data"].map((item) => (
                <div
                  key={item}
                  className={`rounded-2xl border p-4 ${
                    isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-white"
                  }`}
                >
                  <ShieldCheck className="h-5 w-5 text-sky-700" />
                  <p className={`mt-3 text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="p-8 md:p-10">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
                Login Access
              </p>
              <h2 className={`mt-2 text-2xl font-semibold ${isDark ? "text-white" : "text-slate-950"}`}>
                Welcome back
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <label className="block">
                <span className={`text-sm font-medium ${isDark ? "text-slate-200" : "text-slate-800"}`}>
                  Email address
                </span>
                <span className="relative mt-2 block">
                  <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    className={`h-12 w-full rounded-2xl border pl-11 pr-4 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${
                      isDark
                        ? "border-slate-700 bg-slate-950 text-white"
                        : "border-slate-200 bg-white text-slate-900"
                    }`}
                  />
                </span>
              </label>

              <label className="block">
                <span className={`text-sm font-medium ${isDark ? "text-slate-200" : "text-slate-800"}`}>
                  Password
                </span>
                <span className="relative mt-2 block">
                  <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    className={`h-12 w-full rounded-2xl border pl-11 pr-4 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${
                      isDark
                        ? "border-slate-700 bg-slate-950 text-white"
                        : "border-slate-200 bg-white text-slate-900"
                    }`}
                  />
                </span>
              </label>

              {error ? (
                <div
                  className={`rounded-2xl border px-4 py-3 text-sm ${
                    isDark
                      ? "border-rose-800 bg-rose-950 text-rose-300"
                      : "border-rose-200 bg-rose-50 text-rose-700"
                  }`}
                >
                  {error}
                </div>
              ) : null}

              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={handlePasswordReset}
                  disabled={isSendingReset}
                  className={`text-sm font-medium ${
                    isSendingReset
                      ? "cursor-not-allowed text-slate-400"
                      : "text-sky-700 hover:text-sky-800"
                  }`}
                >
                  {isSendingReset ? "Sending reset..." : "Forgot password?"}
                </button>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`h-12 w-full rounded-2xl px-5 font-medium text-white transition ${
                  isSubmitting ? "cursor-not-allowed bg-orange-300" : "bg-orange-500 hover:bg-orange-600"
                }`}
              >
                {isSubmitting ? "Signing in..." : "Sign in"}
              </button>
            </form>
          </section>
        </div>
      </div>
    </main>
  );
}
